import { Routes } from '@angular/router';
import { AppLayout } from './app/layout/component/app.layout';
import { authGuard } from '@/app/guards/auth.guard';
import { globalAdminGuard } from '@/app/guards/global-admin.guard';
import { unsavedChangesGuard } from '@/app/guards/unsaved-changes.guard';

/**
 * Every page is lazy (`loadComponent`) so the initial bundle carries only the
 * shell — layout, auth and the guards. The dashboard is the landing route and
 * still loads on demand; its chunk is fetched while the shell paints.
 */
export const appRoutes: Routes = [
    {
        path: '',
        component: AppLayout,
        canActivate: [authGuard],
        children: [
            {
                path: '',
                loadComponent: () =>
                    import('@/app/pages/dashboard/dashboard').then((m) => m.Dashboard),
            },
            {
                path: 'game-templates',
                loadComponent: () =>
                    import('@/app/pages/game-templates/game-templates').then(
                        (m) => m.GameTemplates,
                    ),
            },
            {
                path: 'website',
                loadComponent: () => import('@/app/pages/website/website').then((m) => m.Website),
            },
            {
                path: 'website/new',
                loadComponent: () =>
                    import('@/app/pages/website/add-site-wizard/add-site-wizard').then(
                        (m) => m.AddSiteWizard,
                    ),
                canActivate: [globalAdminGuard],
                canDeactivate: [unsavedChangesGuard],
            },
            {
                // A flat sibling of `website/:realmId`, not a child of it: that
                // route carries `canDeactivate` and would stay alive underneath.
                // No unsaved-changes guard here — a legitimately backgrounded run
                // would turn every navigation into a bogus leave prompt (§9).
                path: 'website/:realmId/provisioning',
                loadComponent: () =>
                    import('@/app/pages/website/provisioning/provisioning-run').then(
                        (m) => m.ProvisioningRunPage,
                    ),
                canActivate: [globalAdminGuard],
            },
            {
                path: 'website/:realmId/clone',
                loadComponent: () =>
                    import('@/app/pages/website/clone-site/clone-site-wizard').then(
                        (m) => m.CloneSiteWizard,
                    ),
                canActivate: [globalAdminGuard],
                canDeactivate: [unsavedChangesGuard],
            },
            {
                path: 'website/:realmId',
                loadComponent: () => import('@/app/pages/website/website').then((m) => m.Website),
                canDeactivate: [unsavedChangesGuard],
            },
            {
                path: 'companies',
                loadComponent: () =>
                    import('@/app/pages/companies/companies').then((m) => m.Companies),
                canActivate: [globalAdminGuard],
            },
            {
                path: 'categories',
                loadComponent: () =>
                    import('@/app/pages/categories/categories').then((m) => m.Categories),
                canActivate: [globalAdminGuard],
            },
            {
                path: 'site-templates',
                loadComponent: () =>
                    import('@/app/pages/site-templates/site-templates').then(
                        (m) => m.SiteTemplates,
                    ),
                canActivate: [globalAdminGuard],
            },
            {
                path: 'auth-presets',
                loadComponent: () =>
                    import('@/app/pages/auth-presets/auth-presets').then((m) => m.AuthPresets),
                canActivate: [globalAdminGuard],
            },
            {
                path: 'reviews/fake-authors',
                loadComponent: () =>
                    import('@/app/pages/fake-reviews/authors/fake-authors').then(
                        (m) => m.FakeAuthors,
                    ),
                canActivate: [globalAdminGuard],
            },
            {
                path: 'reviews/fake-comments',
                loadComponent: () =>
                    import('@/app/pages/fake-reviews/comments/fake-comments').then(
                        (m) => m.FakeComments,
                    ),
                canActivate: [globalAdminGuard],
            },
            {
                path: 'users',
                loadComponent: () => import('@/app/pages/users/users').then((m) => m.Users),
            },
            {
                path: 'invoices',
                loadComponent: () =>
                    import('@/app/pages/invoices/invoices').then((m) => m.Invoices),
            },
            {
                path: 'help/invoices',
                loadComponent: () =>
                    import('@/app/pages/help/invoices-guide').then((m) => m.InvoicesGuide),
            },
            {
                path: 'automation/templates',
                loadComponent: () =>
                    import('@/app/pages/automation/templates/templates-list').then(
                        (m) => m.TemplatesList,
                    ),
                canActivate: [globalAdminGuard],
            },
            {
                path: 'automation/templates/:id',
                loadComponent: () =>
                    import('@/app/pages/automation/templates/template-editor').then(
                        (m) => m.TemplateEditor,
                    ),
                canActivate: [globalAdminGuard],
            },
            {
                path: 'automation/rules',
                loadComponent: () =>
                    import('@/app/pages/automation/rules/rules-list').then((m) => m.RulesList),
                canActivate: [globalAdminGuard],
            },
            {
                path: 'automation/rules/:id',
                loadComponent: () =>
                    import('@/app/pages/automation/rules/rule-editor').then((m) => m.RuleEditor),
                canActivate: [globalAdminGuard],
            },
            {
                path: 'automation/events',
                loadComponent: () =>
                    import('@/app/pages/automation/events/event-tools').then((m) => m.EventTools),
                canActivate: [globalAdminGuard],
            },
            {
                path: 'skins/purchases',
                loadComponent: () =>
                    import('@/app/pages/skin-purchases/skin-purchases').then(
                        (m) => m.SkinPurchases,
                    ),
            },
            {
                path: 'skins/trade-ins',
                loadComponent: () =>
                    import('@/app/pages/skin-trade-ins/skin-trade-ins').then((m) => m.SkinTradeIns),
            },
        ],
    },
    {
        path: 'notfound',
        loadComponent: () => import('@/app/pages/notfound/notfound').then((m) => m.Notfound),
    },
    {
        path: 'auth',
        loadChildren: () => import('@/app/pages/auth/auth.routes'),
    },
    { path: '**', redirectTo: '/notfound' },
];
